import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import client from './client';
import type { SprintDto, SprintVelocityDto } from '../types/api';

export const sprintKeys = {
  all:      ()               => ['sprints']              as const,
  list:     (teamId?: string) => ['sprints', 'list', teamId ?? null] as const,
  detail:   (id: string)     => ['sprints', id]          as const,
  velocity: (id: string)     => ['sprints', id, 'velocity'] as const,
};

export function useSprintList(teamId?: string) {
  return useQuery({
    queryKey: sprintKeys.list(teamId),
    queryFn: () =>
      client
        .get<SprintDto[]>('/sprints', { params: teamId ? { teamId } : undefined })
        .then((r) => r.data),
  });
}

export function useSprint(id: string) {
  return useQuery({
    queryKey: sprintKeys.detail(id),
    queryFn: () => client.get<SprintDto>(`/sprints/${id}`).then((r) => r.data),
    enabled: !!id,
  });
}

export function useSprintVelocity(id: string) {
  return useQuery({
    queryKey: sprintKeys.velocity(id),
    queryFn: () =>
      client.get<SprintVelocityDto>(`/sprints/${id}/velocity`).then((r) => r.data),
    enabled: !!id,
  });
}

export function useCreateSprint() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      teamId: string;
      name: string;
      goal?: string;
      startDate: string;
      endDate: string;
    }) => client.post<SprintDto>('/sprints', body).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: sprintKeys.all() }),
  });
}

export function useUpdateSprint(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      name?: string;
      goal?: string;
      startDate?: string;
      endDate?: string;
      activate?: boolean;
      complete?: boolean;
      capacityPoints?: number;
      showAndTellDate?: string;
      showAndTellNotes?: string;
    }) => client.patch<SprintDto>(`/sprints/${id}`, body).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: sprintKeys.detail(id) });
      qc.invalidateQueries({ queryKey: sprintKeys.all() });
    },
  });
}

export function useDeleteSprint() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => client.delete(`/sprints/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: sprintKeys.all() }),
  });
}
